/** Time zones for pickers and clocks, taken from the browser's own IANA database. */

export interface TimeZoneOption {
  /** IANA id, e.g. `Asia/Karachi` — what the server stores. */
  id: string;
  /** First segment of the id (`Asia`), used to group the list. */
  region: string;
  /** Current offset from GMT, e.g. `GMT+05:00`. */
  offset: string;
  /** `Asia/Karachi (GMT+05:00)`. */
  label: string;
}

let cache: TimeZoneOption[] | null = null;

/** The device's own zone; UTC when the browser will not say. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function isValidTimeZone(zone: string | null | undefined): zone is string {
  if (zone === null || zone === undefined || zone.trim() === '') {
    return false;
  }
  try {
    new Intl.DateTimeFormat(undefined, { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** `GMT+05:00` for a zone at a moment (offsets move with daylight saving). */
export function gmtOffset(zone: string, at: Date = new Date()): string {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'longOffset' })
      .formatToParts(at)
      .find((candidate) => candidate.type === 'timeZoneName');
    return part === undefined || part.value === 'GMT' ? 'GMT+00:00' : part.value;
  } catch {
    return 'GMT';
  }
}

/**
 * Every zone the browser knows, sorted by region then name. Computed once:
 * formatting ~400 offsets on every render of a form would be wasted work.
 */
export function listTimeZones(): TimeZoneOption[] {
  if (cache !== null) {
    return cache;
  }
  const ids = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  // Browsers list canonical zones and often leave UTC out; schools still use it.
  const all = ids.includes('UTC') ? [...ids] : ['UTC', ...ids];
  const now = new Date();
  cache = all
    .map((id) => {
      const offset = gmtOffset(id, now);
      return {
        id,
        region: id.includes('/') ? id.slice(0, id.indexOf('/')) : 'Other',
        offset,
        label: `${id.replaceAll('_', ' ')} (${offset})`,
      };
    })
    .sort((a, b) => a.region.localeCompare(b.region) || a.id.localeCompare(b.id));
  return cache;
}
